/**
 * AI Studio Page
 *
 * Main page for the AI-powered image generation studio with automation capabilities.
 * Features tabs for Generate (image generation) and Automation (scheduled publishing).
 *
 * Layout:
 * - Top: Tab navigation (Generate / Automation)
 * - Generate Tab:
 *   - Left panel (flex-1): Workflow progress, preview grid with generated images
 *   - Right sidebar (320px): Reference person selector, batch size, generate button
 * - Automation Tab: Full-width automation management UI
 *
 * Uses hooks:
 * - useAIStudio: Main state management for generation workflow
 * - useAIStudioPreferences: User preferences management
 */

import { useState, useCallback } from 'react';
import { Settings, Sparkles, Info, User, LayoutGrid, Trash2, X, Image, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BatchSizeSelector,
  PreviewGrid,
  SettingsPanel,
  ReferencePersonSelector,
  WorkflowProgress,
  ImagePreviewModal,
} from '@/components/ai-studio';
import { AutomationTab } from '@/components/automation/AutomationTab';
import type { Id } from '../../convex/_generated/dataModel';
import { useAIStudio } from '@/hooks/useAIStudio';
import { useAIStudioPreferences } from '@/hooks/useAIStudioPreferences';

export function AIStudioPage() {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'generate' | 'automation'>('generate');

  // Main AI Studio state
  const {
    batchSize,
    setBatchSize,
    selectedReferencePersonId,
    selectReferencePerson,
    sessions,
    selectedSessionIds,
    isGenerating,
    error,
    generate,
    selectSession,
    clearSelectedSessions,
    discardSession,
    bulkDeleteSessions,
    downloadSession,
    clearError,
  } = useAIStudio();

  // Preferences management
  const {
    preferences,
    defaultPreferences,
    updatePreferences,
    isSaving: isSavingPreferences,
  } = useAIStudioPreferences();

  // Track preview session for fullscreen modal
  const [previewSessionId, setPreviewSessionId] = useState<Id<'aiStudioSessions'> | undefined>(
    undefined
  );

  // Get the session to preview in modal
  const previewSession = previewSessionId
    ? (sessions.find(s => s._id === previewSessionId) ?? null)
    : null;

  const handleGenerate = () => {
    generate();
  };

  const handleSelectSession = (sessionId: Id<'aiStudioSessions'>) => {
    selectSession(sessionId);
  };

  const handleBulkDelete = () => {
    bulkDeleteSessions(selectedSessionIds);
  };

  const handlePreviewSession = (sessionId: Id<'aiStudioSessions'>) => {
    setPreviewSessionId(sessionId);
  };

  const handleClosePreview = () => {
    setPreviewSessionId(undefined);
  };

  const handlePreferencesChange = async (
    newPreferences: Parameters<typeof updatePreferences>[0]
  ) => {
    await updatePreferences(newPreferences);
  };

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value as 'generate' | 'automation');
  }, []);

  // Get current preferences input for settings panel
  const currentPreferencesInput = preferences
    ? {
        mode: preferences.mode,
        batchSize: preferences.batchSize,
        enabledCategories: preferences.enabledCategories,
        enabledDimensions: preferences.enabledDimensions,
      }
    : defaultPreferences;

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      {/* Top Navigation Tabs */}
      <div className="border-b border-border bg-card/50 px-6 py-3">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="grid w-[300px] grid-cols-2">
            <TabsTrigger value="generate" className="gap-2">
              <Image className="h-4 w-4" />
              Generate
            </TabsTrigger>
            <TabsTrigger value="automation" className="gap-2">
              <Zap className="h-4 w-4" />
              Automation
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Tab Content */}
      {activeTab === 'automation' ? (
        <div className="flex-1 overflow-y-auto">
          <AutomationTab />
        </div>
      ) : (
        <div className="flex flex-1 min-h-0">
          {/* Left Panel - Main Content */}
          <div className="flex-1 min-w-0 p-6 flex flex-col gap-6">
            {/* Workflow Progress */}
            <WorkflowProgress />

            {/* Content Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Generated Images</h2>
                <p className="text-sm text-muted-foreground">
                  {sessions.length > 0
                    ? `${sessions.length} ${sessions.length === 1 ? 'image' : 'images'} generated`
                    : 'Your AI-generated images will appear here'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* Bulk Delete Controls - Show when images are selected */}
                {selectedSessionIds.length > 0 && (
                  <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5">
                    <span className="text-sm font-medium">
                      {selectedSessionIds.length} selected
                    </span>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={clearSelectedSessions}
                      className="size-6"
                      title="Clear selection"
                    >
                      <X className="size-3.5" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleBulkDelete}
                      disabled={isGenerating}
                      className="h-7 gap-1.5 px-2"
                    >
                      <Trash2 className="size-3.5" />
                      Delete
                    </Button>
                  </div>
                )}
                {sessions.length > 0 && (
                  <span className="rounded-full bg-muted px-2 py-1 text-xs font-medium">
                    {sessions.length} / 3
                  </span>
                )}
              </div>
            </div>

            {/* Preview Grid */}
            <div className="flex items-start justify-start">
              <PreviewGrid
                sessions={sessions}
                isGenerating={isGenerating}
                batchSize={batchSize}
                onSelectSession={handleSelectSession}
                onDiscardSession={discardSession}
                onDownloadSession={downloadSession}
                onPreviewSession={handlePreviewSession}
                selectedSessionIds={selectedSessionIds}
              />
            </div>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Status Hint - Always visible */}
            <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-4">
              <div className="flex size-10 items-center justify-center rounded-full bg-primary/10">
                <Info className="size-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">
                  {isGenerating
                    ? 'Generating images...'
                    : sessions.length > 0
                      ? 'Images generated'
                      : 'Ready to generate'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isGenerating
                    ? 'Please wait while AI creates your portraits'
                    : sessions.length > 0
                      ? 'Select images to upscale or download'
                      : 'Select a reference person and click Generate to create AI portraits'}
                </p>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="p-4 bg-destructive/10 text-destructive rounded-lg flex items-center justify-between">
                <span>{error}</span>
                <Button variant="ghost" size="sm" onClick={clearError}>
                  Dismiss
                </Button>
              </div>
            )}
          </div>

          {/* Right Sidebar - Generation Settings */}
          <div className="w-[320px] shrink-0 border-l border-border bg-card/50 p-5 flex flex-col gap-5 overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-foreground">Generation Settings</h2>
              <Button
                variant="ghost"
                size="icon"
                className="size-6"
                onClick={() => setSettingsOpen(true)}
                title="Settings"
              >
                <Settings className="size-4" />
              </Button>
            </div>

            {/* Reference Person */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <User className="size-4 text-muted-foreground" />
                <span className="text-sm text-foreground">Reference Person</span>
              </div>
              <ReferencePersonSelector
                selectedPersonId={selectedReferencePersonId}
                onSelect={selectReferencePerson}
                disabled={isGenerating}
              />
            </div>

            {/* Batch Size */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <LayoutGrid className="size-4 text-muted-foreground" />
                <span className="text-sm text-foreground">Batch Size</span>
              </div>
              <BatchSizeSelector
                batchSize={batchSize}
                onBatchSizeChange={setBatchSize}
                disabled={isGenerating}
                showLabel={false}
              />
            </div>

            {/* Advanced Settings Link */}
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              className="flex items-center justify-between py-2 text-xs text-muted-foreground hover:text-foreground transition-colors border-t border-border mt-1 pt-3"
            >
              <span>Advanced Settings</span>
              <Settings className="size-4" />
            </button>

            {/* Spacer to push button to bottom */}
            <div className="flex-1" />

            {/* Generate Button */}
            <Button
              className="w-full h-10 bg-[#B8EF70] text-black hover:bg-[#a8df60] text-sm font-semibold"
              onClick={handleGenerate}
              disabled={isGenerating || !selectedReferencePersonId}
            >
              <Sparkles className="size-4" />
              {isGenerating ? 'Generating...' : 'Generate Images'}
            </Button>
          </div>
        </div>
      )}

      {/* Settings Panel - rendered regardless of active tab */}
      <SettingsPanel
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        preferences={currentPreferencesInput}
        onPreferencesChange={handlePreferencesChange}
        isSaving={isSavingPreferences}
      />

      {/* Image Preview Modal - rendered regardless of active tab */}
      <ImagePreviewModal
        session={previewSession}
        isOpen={!!previewSessionId}
        onClose={handleClosePreview}
        onDownload={() => previewSessionId && downloadSession(previewSessionId)}
        onDelete={() => previewSessionId && discardSession(previewSessionId)}
      />
    </div>
  );
}
